import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { AuthShell, Field } from "@/pages/Login";

export default function Signup() {
  const { register, user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/console", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await register(name.trim(), email.trim(), password);
      if (data?.requires_confirmation) {
        navigate(`/confirm?email=${encodeURIComponent(email.trim())}`, { replace: true });
        return;
      }
      navigate("/console", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <span className="chip-brand" data-testid="signup-chip">
          / 01 · create account
        </span>

        <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold text-white leading-[0.95] tracking-tighter">
          Start your garage.
        </h1>
        <p className="mt-4 text-white/50 text-sm font-light leading-relaxed">
          Free forever. No credit card. VIN-lock up to 3 vehicles.
        </p>

        <div className="mt-10 space-y-5">
          <Field
            id="name"
            testId="signup-name"
            label="Name"
            required
            autoComplete="name"
            value={name}
            onChange={setName}
          />
          <Field
            id="email"
            testId="signup-email"
            label="Email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={setEmail}
          />
          <Field
            id="password"
            testId="signup-password"
            label="Password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
          />
          <p className="mt-2 font-mono-tech text-[10px] uppercase tracking-widest text-white/35">
            Min 8 characters (AWS Cognito)
          </p>
        </div>

        {error && (
          <div
            data-testid="signup-error"
            className="mt-5 border border-mclaren/40 bg-mclaren/5 px-4 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-mclaren"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="signup-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-3 h-12 px-6 bg-mclaren text-void font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-sunset transition-all disabled:opacity-60"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Creating
            </>
          ) : (
            <>
              Create account <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </>
          )}
        </button>

        <p className="mt-6 text-white/50 text-sm">
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="signup-login-link"
            className="text-cyan-electric hover:text-white underline underline-offset-4"
          >
            Sign in
          </Link>
        </p>
      </motion.form>
    </AuthShell>
  );
}
